import { format as formatLocalDate } from 'date-fns';
import type {
  EquipmentRepairRow,
  Vehicle,
  VehicleDefectExportRow,
  VehiclePmInspection,
  VehiclePmInspectionItem
} from '../../../types/Vehicle';
import { escapeHtml } from '../../schedule/utils/schedulePrint';
import { loadDawsonRoadMaintenanceLogoDataUrl } from '../../schedule/utils/scheduleWeeklyMaintenancePrint';
import { formatDate, formatDateTime } from '../../../utils/wrapDate';
import {
  DEFECT_OUTCOME_LABEL,
  defectParts,
  defectSourceLabel,
  defectStatusLabel,
  personName,
  serviceAreaLabel
} from './equipmentDisplay';

/** The printed empty marker (FMT-10): an en dash, never an em dash (LANG-05). */
const EMPTY = '–';

/**
 * Print-preview reports for a unit: the defect export, PM inspection sheets and repairs.
 * Same approach as the schedule prints: build a standalone HTML page, open it in a new
 * window and let the browser print / save as PDF.
 */

/** YYYY-MM-DD in Pacific Time (FMT-01, FMT-24). */
function fmtDate(value?: string | Date | null): string {
  return formatDate(value ?? null) ?? EMPTY;
}

/** A report range bound: the calendar day the user picked (a local start / end of day). */
function fmtDay(value: Date): string {
  return formatLocalDate(value, 'yyyy-MM-dd');
}

function fmtNumber(value?: number | null, unit?: string): string {
  if (value == null) return EMPTY;
  const number = value.toLocaleString('en-CA');
  return unit ? `${number} ${unit}` : number;
}

function text(value?: string | number | null): string {
  if (value == null || value === '') return EMPTY;
  return escapeHtml(String(value));
}

function serviceAreas(vehicle: Vehicle): string {
  return (vehicle.vehicleServiceAreas || []).map((sa) => serviceAreaLabel(sa.serviceArea)).join(', ');
}

/** km or hours from the Vehicle Profile's odometer alerts (EQ-19). */
export function odometerUnitOf(vehicle: Pick<Vehicle, 'vehicleProfile'>): 'km' | 'hours' {
  return /hour|hr/i.test(vehicle.vehicleProfile?.odometerAlerts ?? '') ? 'hours' : 'km';
}

/** "$45.00 / h" (EQ-26): the unit of measure in lowercase, "h" for hours. Null when unknown. */
export function formatRate(rate?: number | null, unitOfMeasure?: string | null): string | null {
  if (rate == null) return null;
  const amount = rate.toLocaleString('en-CA', { style: 'currency', currency: 'CAD', currencyDisplay: 'narrowSymbol' });
  const unit = (unitOfMeasure || '').trim().toLowerCase();
  if (!unit) return amount;
  const short = /^(h|hr|hrs|hour|hours)$/.test(unit) ? 'h' : unit;
  return `${amount} / ${short}`;
}

/** Hours worked, "4.5 h"; null when unknown (never 0 for unknown). */
export function formatHours(hours?: number | null): string | null {
  if (hours == null) return null;
  return `${hours.toLocaleString('en-CA', { maximumFractionDigits: 2 })} h`;
}

/** Repair status in words: no raw codes such as "imported" or "APPROVED". */
export function repairStatusLabel(status?: string | null): string | null {
  const value = (status || '').trim();
  if (!value) return null;
  return value
    .replace(/[_-]+/g, ' ')
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function makeModelYear(vehicle: Vehicle): string {
  return [[vehicle.make, vehicle.model].filter(Boolean).join(' '), vehicle.year ? String(vehicle.year) : '']
    .filter(Boolean)
    .join(' · ');
}

const BASE_CSS = `
  * { box-sizing: border-box; }
  body {
    font-family: "Segoe UI", Arial, Helvetica, sans-serif;
    color: #1f2933;
    margin: 0;
    padding: 24px;
    font-size: 11px;
  }
  .page { page-break-after: always; break-after: page; }
  .page:last-child { page-break-after: auto; break-after: auto; }
  .head { display: flex; align-items: center; justify-content: space-between; gap: 16px; border-bottom: 3px solid #1f4e79; padding-bottom: 10px; margin-bottom: 14px; }
  .head img { max-height: 46px; }
  .title { font-size: 18px; font-weight: 700; letter-spacing: .04em; color: #1f4e79; text-transform: uppercase; }
  .subtitle { font-size: 11px; color: #52606d; margin-top: 2px; }
  .head-right { text-align: right; }
  .section-title { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: #1f4e79; margin: 14px 0 6px; }
  .meta { width: 100%; border-collapse: collapse; margin-bottom: 4px; }
  .meta td { border: 1px solid #cbd2d9; padding: 5px 8px; vertical-align: top; }
  .meta .label { background: #f0f4f8; font-weight: 600; width: 16%; color: #3e4c59; white-space: nowrap; }
  .meta .value { width: 34%; }
  .summary { display: flex; gap: 8px; margin: 10px 0 4px; }
  .chip { border: 1px solid #cbd2d9; border-radius: 6px; padding: 6px 12px; min-width: 90px; }
  .chip .n { font-size: 16px; font-weight: 700; }
  .chip .l { font-size: 9px; text-transform: uppercase; letter-spacing: .05em; color: #52606d; }
  .chip.fail { border-color: #e12d39; color: #b0222d; }
  .chip.pass { border-color: #3ebd93; color: #147d64; }
  table.data { width: 100%; border-collapse: collapse; }
  table.data th, table.data td { border: 1px solid #cbd2d9; padding: 4px 6px; vertical-align: top; text-align: left; word-wrap: break-word; }
  table.data th { background: #1f4e79; color: #fff; font-weight: 600; font-size: 10px; text-transform: uppercase; letter-spacing: .03em; }
  table.data tr.group td { background: #e4ecf5; font-weight: 700; color: #1f4e79; }
  table.data tr.is-fail td { background: #fff1f1; }
  table.data tbody tr { page-break-inside: avoid; }
  thead { display: table-header-group; }
  .badge { display: inline-block; border-radius: 10px; padding: 1px 8px; font-size: 10px; font-weight: 700; }
  .badge.pass { background: #e3f9e5; color: #147d64; }
  .badge.fail { background: #ffe3e3; color: #b0222d; }
  .badge.other { background: #eef2f7; color: #3e4c59; }
  .num { text-align: right; white-space: nowrap; }
  .nowrap { white-space: nowrap; }
  .muted { color: #7b8794; }
  .attention { border: 1px solid #e12d39; border-left-width: 4px; border-radius: 4px; padding: 6px 10px; margin: 10px 0; background: #fff8f8; }
  .attention ul { margin: 4px 0 0 16px; padding: 0; }
  .signatures { display: flex; gap: 24px; margin-top: 28px; }
  .signatures div { flex: 1; border-top: 1px solid #52606d; padding-top: 4px; font-size: 10px; color: #52606d; }
  .footer { margin-top: 16px; font-size: 9px; color: #7b8794; border-top: 1px solid #cbd2d9; padding-top: 6px; display: flex; justify-content: space-between; }
  .empty { padding: 24px; text-align: center; color: #7b8794; border: 1px dashed #cbd2d9; }
  @media print { body { padding: 0; } }
`;

function headerHtml(logo: string | null, title: string, subtitle: string, right: string): string {
  return `
    <div class="head">
      <div>
        ${logo ? `<img src="${logo}" alt="Dawson Road Maintenance" />` : ''}
        <div class="title">${escapeHtml(title)}</div>
        <div class="subtitle">${subtitle}</div>
      </div>
      <div class="head-right">${right}</div>
    </div>`;
}

function vehicleMetaHtml(vehicle: Vehicle): string {
  return `
    <table class="meta" aria-label="Unit">
      <tr>
        <td class="label">Unit #</td><td class="value"><strong>${text(vehicle.unitNumber)}</strong></td>
        <td class="label">Description</td><td class="value">${text(vehicle.description)}</td>
      </tr>
      <tr>
        <td class="label">Make / Model / Year</td><td class="value">${text(makeModelYear(vehicle))}</td>
        <td class="label">Classification</td><td class="value">${text(vehicle.vehicleClassification)}</td>
      </tr>
      <tr>
        <td class="label">VIN</td><td class="value">${text(vehicle.vinNumber)}</td>
        <td class="label">Plate</td><td class="value">${text(vehicle.registrationNumber)}</td>
      </tr>
      <tr>
        <td class="label">Foreman Area</td><td class="value">${text(vehicle.foremanArea?.name)}</td>
        <td class="label">Service Area</td><td class="value">${text(serviceAreas(vehicle))}</td>
      </tr>
    </table>`;
}

function footerHtml(left: string): string {
  return `<div class="footer"><span>${escapeHtml(left)}</span><span>Printed ${escapeHtml(formatDateTime(new Date()) ?? '')}</span></div>`;
}

function documentHtml(
  title: string,
  body: string,
  landscape: boolean,
  extra: { css?: string; margin?: string; beforePrint?: string } = {}
): string {
  return `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(title)}</title>
    <style>
      @page { size: letter ${landscape ? 'landscape' : 'portrait'}; margin: ${extra.margin || '12mm'}; }
      ${BASE_CSS}
      ${extra.css || ''}
    </style>
  </head>
  <body>
    ${body}
    <script>
      (function () {
        function run() {
          ${extra.beforePrint || ''}
          setTimeout(function () { window.print(); }, 250);
        }
        if (document.readyState === 'complete') run();
        else window.addEventListener('load', run);
      })();
    </script>
  </body>
</html>`;
}

/** Opens the window synchronously (before any await) so popup blockers allow it. */
function openPrintWindow(): Window | null {
  const win = window.open('', '_blank', 'width=1000,height=1100');
  if (win) {
    win.document.write('<p style="font-family: sans-serif; padding: 24px;">Preparing report…</p>');
  }
  return win;
}

function writeToWindow(win: Window, html: string): void {
  win.document.open();
  win.document.write(html);
  win.document.close();
  win.focus();
}

// ---------------------------------------------------------------------------------------------
// Defect export

export function buildDefectReportHtml(params: {
  vehicle: Vehicle;
  defects: VehicleDefectExportRow[];
  from: Date;
  to: Date;
  logoDataUrl: string | null;
}): string {
  const { vehicle, defects, from, to, logoDataUrl } = params;
  const open = defects.filter((d) => d.status === 'Open').length;
  const range = `${fmtDay(from)} – ${fmtDay(to)}`;

  const rows = defects
    .map((defect) => {
      const { category, defect: what, comments } = defectParts(defect);
      // Fixed By / Date Fixed (EQ-07); older rows only carry the remedy or close fields.
      const fixedBy =
        personName(defect.fixedBy) || personName(defect.remediedBy) || (defect.remedy ? personName(defect.closedBy) : '');
      const fixedAt = defect.fixedAt || defect.remediedAt || (defect.remedy ? defect.closedAt : null);
      const outcome =
        defect.outcome && defect.outcome !== 'Repaired' ? DEFECT_OUTCOME_LABEL[defect.outcome] ?? defect.outcome : '';
      const badge = defect.status === 'Open' ? 'fail' : defect.status === 'Closed' ? 'pass' : 'other';
      const remedy = [outcome ? `<strong>${escapeHtml(outcome)}</strong>` : '', defect.remedy ? escapeHtml(defect.remedy) : '']
        .filter(Boolean)
        .join('<br />');
      return `
        <tr>
          <td class="nowrap">${fmtDate(defect.reportedAt)}</td>
          <td class="num">${fmtNumber(defect.odometer)}</td>
          <td>${text(defectSourceLabel(defect.source))}</td>
          <td>${text(defect.reporterName)}</td>
          <td>${text(category)}</td>
          <td>${text(what)}${comments ? `<div class="muted">${escapeHtml(comments)}</div>` : ''}</td>
          <td>${text(defect.severity)}</td>
          <td><span class="badge ${badge}">${text(defectStatusLabel(String(defect.status)))}</span></td>
          <td>${remedy || EMPTY}</td>
          <td>${text(fixedBy)}</td>
          <td class="nowrap">${fmtDate(fixedAt)}</td>
          <td>${text(personName(defect.closedBy))}</td>
          <td class="nowrap">${fmtDate(defect.closedAt)}</td>
        </tr>`;
    })
    .join('');

  const body = `
    <div class="page">
      ${headerHtml(
        logoDataUrl,
        'Defect Report',
        `Unit ${text(vehicle.unitNumber)} · ${escapeHtml(range)}`,
        `<div class="muted">Reported Between</div><strong>${escapeHtml(range)}</strong>`
      )}
      <div class="section-title">Unit</div>
      ${vehicleMetaHtml(vehicle)}
      <div class="summary">
        <div class="chip"><div class="n">${defects.length}</div><div class="l">Defects</div></div>
        <div class="chip fail"><div class="n">${open}</div><div class="l">Open</div></div>
        <div class="chip pass"><div class="n">${defects.length - open}</div><div class="l">Closed</div></div>
      </div>
      <div class="section-title">Defects</div>
      ${
        defects.length === 0
          ? '<div class="empty">No defects were reported on this unit in the selected date range.</div>'
          : `<table class="data" aria-label="Defects">
              <thead>
                <tr>
                  <th style="width:7%">Date</th>
                  <th style="width:6%">Odometer</th>
                  <th style="width:7%">Reported On</th>
                  <th style="width:8%">Reporter</th>
                  <th style="width:8%">Category</th>
                  <th style="width:16%">Defect</th>
                  <th style="width:5%">Severity</th>
                  <th style="width:8%">Status</th>
                  <th style="width:11%">Remedy</th>
                  <th style="width:7%">Fixed By</th>
                  <th style="width:6%">Date Fixed</th>
                  <th style="width:7%">Closed By</th>
                  <th style="width:6%">Date Closed</th>
                </tr>
              </thead>
              <tbody>${rows}</tbody>
            </table>`
      }
      ${footerHtml(`Unit ${vehicle.unitNumber} · Defect Report`)}
    </div>`;

  return documentHtml(`Defects ${vehicle.unitNumber}`, body, true);
}

export async function printDefectReport(params: {
  vehicle: Vehicle;
  load: () => Promise<VehicleDefectExportRow[]>;
  from: Date;
  to: Date;
}): Promise<void> {
  const win = openPrintWindow();
  if (!win) throw new Error('Allow pop-ups for this site to print the report.');
  try {
    const [defects, logoDataUrl] = await Promise.all([params.load(), loadDawsonRoadMaintenanceLogoDataUrl()]);
    writeToWindow(win, buildDefectReportHtml({ ...params, defects, logoDataUrl }));
  } catch (error) {
    win.close();
    throw error;
  }
}

// ---------------------------------------------------------------------------------------------
// PM inspections

/**
 * One PM per printed page. The checklist is laid out in two columns and the page shrinks its
 * type (PM_FIT_SCRIPT) until it fits, so a 60+ item Major A still lands on a single sheet.
 */
const PM_PAGE_CSS = `
  .pm-page {
    --fs: 9px;
    width: 7.85in;
    margin: 0 auto;
    font-size: var(--fs);
    overflow: hidden;
  }
  .pm-page .head { padding-bottom: 6px; margin-bottom: 8px; border-bottom-width: 2px; }
  .pm-page .head img { max-height: 32px; }
  .pm-page .title { font-size: 15px; }
  .pm-page .tally { font-size: 10px; text-align: right; line-height: 1.5; }
  .pm-page .tally b { font-size: 13px; }
  .pm-page .tally .p { color: #147d64; }
  .pm-page .tally .f { color: #b0222d; }
  .pm-page .meta { margin-bottom: 6px; }
  .pm-page .meta td { padding: 3px 6px; }
  .pm-page .meta .label { width: 11%; font-size: 0.92em; }
  .pm-page .meta .value { width: 22.3%; }
  .pm-page .attention { margin: 6px 0; padding: 4px 8px; }
  .pm-page .attention ul { margin-top: 2px; }
  .pm-cols { display: flex; gap: 10px; align-items: flex-start; }
  .pm-col { flex: 1 1 0; min-width: 0; }
  .pm-items { width: 100%; border-collapse: collapse; table-layout: fixed; }
  .pm-items td { border-bottom: 1px solid #e4e7eb; padding: 2px 4px; vertical-align: top; line-height: 1.25; }
  .pm-items .grp td { background: #1f4e79; color: #fff; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; font-size: 0.92em; padding: 3px 4px; border-bottom: 0; }
  .pm-items .res-col { width: 3.4em; }
  .pm-items .res { text-align: center; }
  .pm-items tr.is-fail td { background: #fff1f1; }
  .pm-items .meas { font-weight: 600; color: #1f4e79; white-space: nowrap; }
  .pm-items .note { color: #52606d; font-style: italic; }
  .mark { display: inline-block; min-width: 24px; border-radius: 3px; padding: 0 3px; font-size: 0.85em; font-weight: 700; }
  .mark.pass { background: #e3f9e5; color: #147d64; }
  .mark.fail { background: #b0222d; color: #fff; }
  .mark.other { background: #eef2f7; color: #3e4c59; }
  .pm-page .signatures { margin-top: 14px; gap: 16px; }
  .pm-page .footer { margin-top: 8px; }
  @media print { .pm-page { margin: 0; } }
`;

/** Shrinks each PM page's type until it fits the printable height of a letter sheet. */
const PM_FIT_SCRIPT = `
  var limit = 10.3 * 96;
  document.querySelectorAll('.pm-page').forEach(function (page) {
    var size = 9;
    while (page.scrollHeight > limit && size > 6) {
      size -= 0.25;
      page.style.setProperty('--fs', size + 'px');
    }
  });
`;

function resultMark(value: string): string {
  const v = (value || '').trim().toLowerCase();
  const cls = v === 'pass' ? 'pass' : v === 'fail' ? 'fail' : 'other';
  return `<span class="mark ${cls}">${text(value)}</span>`;
}

type PmRow = { group: string } | { item: VehiclePmInspectionItem };

/** Rough printed height of an item, in lines, used to balance the two columns. */
function itemWeight(item: VehiclePmInspectionItem): number {
  const measured = [item.measurementValue, item.unitOfMeasurement].filter(Boolean).join(' ');
  const lineChars = 90;
  const main = Math.ceil((item.inspectionItem.length + (measured ? measured.length + 3 : 0)) / lineChars);
  const note = item.comments ? Math.ceil(item.comments.length / lineChars) : 0;
  return Math.max(1, main) + note;
}

/**
 * Split the checklist into two columns of roughly equal height, keeping recorded order.
 * A section that straddles the split repeats its heading, marked "(cont.)", in column two.
 */
export function splitPmColumns(items: VehiclePmInspectionItem[]): [PmRow[], PmRow[]] {
  const groupWeight = 1.2;
  let total = 0;
  let previous = '';
  items.forEach((item) => {
    if (item.inspectionType !== previous) total += groupWeight;
    previous = item.inspectionType;
    total += itemWeight(item);
  });

  const left: PmRow[] = [];
  const right: PmRow[] = [];
  let used = 0;
  let target = left;
  previous = '';
  items.forEach((item) => {
    const newGroup = item.inspectionType !== previous;
    const cost = itemWeight(item) + (newGroup ? groupWeight : 0);
    // Move to column two at the halfway mark, or early rather than leave a section's heading
    // and only a line or two of it stranded at the foot of column one.
    const half = total / 2;
    const strands = newGroup && used + groupWeight + 3 > half;
    if (target === left && used > 0 && (used + cost / 2 > half || strands)) {
      target = right;
      if (!newGroup) right.push({ group: `${item.inspectionType} (cont.)` });
    }
    if (newGroup) target.push({ group: item.inspectionType });
    target.push({ item });
    used += cost;
    previous = item.inspectionType;
  });
  return [left, right];
}

function pmColumnHtml(rows: PmRow[]): string {
  if (rows.length === 0) return '';
  const body = rows
    .map((row) => {
      if ('group' in row) return `<tr class="grp"><td colspan="2">${text(row.group)}</td></tr>`;
      const { item } = row;
      const isFail = item.inspectionValue.trim().toLowerCase() === 'fail';
      const measured = [item.measurementValue, item.unitOfMeasurement].filter(Boolean).join(' ');
      return `<tr class="${isFail ? 'is-fail' : ''}">
          <td class="res">${resultMark(item.inspectionValue)}</td>
          <td>${text(item.inspectionItem)}${measured ? ` <span class="meas">– ${escapeHtml(measured)}</span>` : ''}${
            item.comments ? `<div class="note">${escapeHtml(item.comments)}</div>` : ''
          }</td>
        </tr>`;
    })
    .join('');
  return `<table class="pm-items"><colgroup><col class="res-col" /><col /></colgroup><tbody>${body}</tbody></table>`;
}

function pmPageHtml(
  vehicle: Vehicle,
  pm: VehiclePmInspection,
  logoDataUrl: string | null,
  typeLabel: (code: string) => string
): string {
  const pmType = typeLabel(pm.pmType);
  const items = pm.items || [];
  const fails = items.filter((item) => item.inspectionValue.trim().toLowerCase() === 'fail');
  const passes = items.filter((item) => item.inspectionValue.trim().toLowerCase() === 'pass');
  const other = items.length - passes.length - fails.length;
  const [left, right] = splitPmColumns(items);

  const tally = `
    <div class="tally">
      <div><b>${items.length}</b> items</div>
      <div><span class="p"><b>${passes.length}</b> pass</span> · <span class="f"><b>${fails.length}</b> fail</span>${
        other > 0 ? ` · <b>${other}</b> other` : ''
      }</div>
    </div>`;

  return `
    <div class="page pm-page">
      ${headerHtml(
        logoDataUrl,
        'PM Inspection',
        `Unit ${text(vehicle.unitNumber)} · ${text(pmType)} · ${fmtDate(pm.performedAt)}`,
        tally
      )}
      <table class="meta" aria-label="Unit and PM details">
        <tr>
          <td class="label">Unit #</td><td class="value"><strong>${text(vehicle.unitNumber)}</strong></td>
          <td class="label">Description</td><td class="value">${text(vehicle.description)}</td>
          <td class="label">Make / Model</td><td class="value">${text(makeModelYear(vehicle))}</td>
        </tr>
        <tr>
          <td class="label">VIN</td><td class="value">${text(vehicle.vinNumber)}</td>
          <td class="label">Plate</td><td class="value">${text(vehicle.registrationNumber)}</td>
          <td class="label">Foreman Area</td><td class="value">${text(vehicle.foremanArea?.name)}</td>
        </tr>
        <tr>
          <td class="label">PM Date</td><td class="value"><strong>${fmtDate(pm.performedAt)}</strong></td>
          <td class="label">PM Type</td><td class="value"><strong>${text(pmType)}</strong></td>
          <td class="label">Odometer</td><td class="value"><strong>${fmtNumber(pm.odometer, odometerUnitOf(vehicle))}</strong></td>
        </tr>
        <tr>
          <td class="label">Employee</td><td class="value">${text(pm.employeeName)}</td>
          <td class="label">Classification</td><td class="value">${text(vehicle.vehicleClassification)}</td>
          <td class="label">Reference #</td><td class="value">${text(pm.referenceNumber)}</td>
        </tr>
      </table>
      ${
        fails.length > 0
          ? `<div class="attention"><strong>Items Requiring Attention</strong><ul>${fails
              .map(
                (item) =>
                  `<li><strong>${text(item.inspectionType)}</strong> – ${text(item.inspectionItem)}${
                    item.comments ? `: <em>${escapeHtml(item.comments)}</em>` : ''
                  }</li>`
              )
              .join('')}</ul></div>`
          : ''
      }
      ${
        items.length === 0
          ? '<div class="empty">No inspection items were recorded on this PM.</div>'
          : `<div class="pm-cols" aria-label="Inspection items">
              <div class="pm-col">${pmColumnHtml(left)}</div>
              <div class="pm-col">${pmColumnHtml(right)}</div>
            </div>`
      }
      <div class="signatures">
        <div>Mechanic Signature</div>
        <div>Reviewed By</div>
        <div>Date</div>
      </div>
      ${footerHtml(`Unit ${vehicle.unitNumber} · ${pmType} · ${fmtDate(pm.performedAt)}`)}
    </div>`;
}

export function buildPmReportHtml(params: {
  vehicle: Vehicle;
  inspections: VehiclePmInspection[];
  logoDataUrl: string | null;
  emptyMessage?: string;
  /** Code -> display label ("Type 1" -> "Type 1 (50 h)"); unknown codes print as stored. */
  pmTypeLabels?: Record<string, string>;
}): string {
  const { vehicle, inspections, logoDataUrl } = params;
  const typeLabel = (code: string) => params.pmTypeLabels?.[code] ?? code;
  const body =
    inspections.length > 0
      ? inspections.map((pm) => pmPageHtml(vehicle, pm, logoDataUrl, typeLabel)).join('')
      : `<div class="page">${headerHtml(logoDataUrl, 'PM Inspection', `Unit ${text(vehicle.unitNumber)}`, '')}
          <div class="empty">${escapeHtml(params.emptyMessage || 'No PMs to print.')}</div></div>`;
  const title =
    inspections.length === 1
      ? `PM ${vehicle.unitNumber} ${typeLabel(inspections[0].pmType)} ${fmtDate(inspections[0].performedAt)}`
      : `PM History ${vehicle.unitNumber}`;
  return documentHtml(title, body, false, { css: PM_PAGE_CSS, margin: '8mm', beforePrint: PM_FIT_SCRIPT });
}

export async function printPmInspections(params: {
  vehicle: Vehicle;
  load: () => Promise<VehiclePmInspection[]>;
  emptyMessage?: string;
  pmTypeLabels?: Record<string, string>;
}): Promise<void> {
  const win = openPrintWindow();
  if (!win) throw new Error('Allow pop-ups for this site to print the PM.');
  try {
    const [inspections, logoDataUrl] = await Promise.all([params.load(), loadDawsonRoadMaintenanceLogoDataUrl()]);
    writeToWindow(
      win,
      buildPmReportHtml({
        vehicle: params.vehicle,
        inspections,
        logoDataUrl,
        emptyMessage: params.emptyMessage,
        pmTypeLabels: params.pmTypeLabels
      })
    );
  } catch (error) {
    win.close();
    throw error;
  }
}

// ---------------------------------------------------------------------------------------------
// Repairs (EQ-26)

/** Repair lines in a date range, newest first: one file, same layout family as the PM print. */
export function buildRepairReportHtml(params: {
  vehicle: Vehicle;
  repairs: EquipmentRepairRow[];
  from: Date;
  to: Date;
  logoDataUrl: string | null;
}): string {
  const { vehicle, repairs, from, to, logoDataUrl } = params;
  const range = `${fmtDay(from)} – ${fmtDay(to)}`;
  const unit = odometerUnitOf(vehicle);
  const showCost = repairs.some((row) => row.hours != null || row.rate != null);
  const totalHours = repairs.reduce((sum, row) => sum + (row.hours ?? 0), 0);

  const rows = repairs
    .map((row) => {
      const task = [row.taskNumber, row.taskName].filter(Boolean).join(' · ');
      return `
        <tr>
          <td class="nowrap">${fmtDate(row.date)}</td>
          <td class="num">${fmtNumber(row.odometer, unit)}</td>
          <td>${text(row.employeeName)}</td>
          <td>${text(task)}</td>
          <td>${text(repairStatusLabel(row.status))}</td>
          <td>${text(row.comments)}</td>
          ${
            showCost
              ? `<td class="num">${text(formatHours(row.hours))}</td><td class="num">${text(formatRate(row.rate, row.unitOfMeasure))}</td>`
              : ''
          }
        </tr>`;
    })
    .join('');

  const body = `
    <div class="page">
      ${headerHtml(
        logoDataUrl,
        'Repair History',
        `Unit ${text(vehicle.unitNumber)} · ${escapeHtml(range)}`,
        `<div class="muted">Repairs Between</div><strong>${escapeHtml(range)}</strong>`
      )}
      <div class="section-title">Unit</div>
      ${vehicleMetaHtml(vehicle)}
      <div class="summary">
        <div class="chip"><div class="n">${repairs.length}</div><div class="l">Repair Lines</div></div>
        ${showCost ? `<div class="chip"><div class="n">${escapeHtml(formatHours(totalHours) ?? EMPTY)}</div><div class="l">Hours</div></div>` : ''}
      </div>
      <div class="section-title">Repairs</div>
      ${
        repairs.length === 0
          ? '<div class="empty">No repairs were recorded on this unit in the selected date range.</div>'
          : `<table class="data" aria-label="Repairs">
              <thead>
                <tr>
                  <th style="width:9%">Date</th>
                  <th style="width:10%">Odometer</th>
                  <th style="width:13%">Employee</th>
                  <th style="width:18%">Task</th>
                  <th style="width:9%">Status</th>
                  <th>Comments</th>
                  ${showCost ? '<th style="width:7%">Hours</th><th style="width:9%">Rate</th>' : ''}
                </tr>
              </thead>
              <tbody>${rows}</tbody>
            </table>`
      }
      <div class="signatures">
        <div>Printed By</div>
        <div>Reviewed By</div>
        <div>Date</div>
      </div>
      ${footerHtml(`Unit ${vehicle.unitNumber} · Repair History · ${range}`)}
    </div>`;

  return documentHtml(`Repairs ${vehicle.unitNumber}`, body, true);
}

export async function printRepairReport(params: {
  vehicle: Vehicle;
  load: () => Promise<EquipmentRepairRow[]>;
  from: Date;
  to: Date;
}): Promise<void> {
  const win = openPrintWindow();
  if (!win) throw new Error('Allow pop-ups for this site to print the report.');
  try {
    const [repairs, logoDataUrl] = await Promise.all([params.load(), loadDawsonRoadMaintenanceLogoDataUrl()]);
    writeToWindow(win, buildRepairReportHtml({ ...params, repairs, logoDataUrl }));
  } catch (error) {
    win.close();
    throw error;
  }
}
